import { useEffect, useState } from 'react';

import config from '@/config';
import { NextButton, PrevButton } from '@/shared-components/buttons/components/SliderControls';

import InlinePostImage from './InlinePostImage';

import './BoardBodyPicture.css';

export default function InlineImageCarousel({
  groupId,
  images,
  onRemoveImage,
  onCaptionChange,
  previewMode = false
}) {
  const [activeSlide, setActiveSlide] = useState(0);

  // ——— PRELOAD ALL SLIDE IMAGES (for faster loading) ———
  useEffect(() => {
    images.filter(img => img.image_id).forEach(img => {
      const pre = new Image();
      pre.src = `${config.app.imageServiceDomain}/${img.image_id}/w=${1200}`; // match your max
    });
  }, [images]);

  // Always call hooks at the top
  useEffect(() => {
    if (activeSlide >= images.length) {
      setActiveSlide(Math.max(0, images.length - 1));
    }
  }, [activeSlide, images.length]);

  // If images array is empty, render nothing
  if (!images?.length) {
    return null;
  }

  // Clamp activeSlide for safe indexing
  const safeIndex = Math.min(activeSlide, images.length - 1);

  const currentImage = images[safeIndex];

  // Navigation handlers
  const handlePrev = () => {
    setActiveSlide(idx => Math.max(0, idx - 1));
  };
  const handleNext = () => {
    setActiveSlide(idx => Math.min(images.length - 1, idx + 1));
  };

  // Slider controls
  const sliderContainer = images.length > 1 && (
    <div
      className="artwork-view-pictures-slider__control-indicators-container"
      contentEditable={false}
    >
      <div className="artwork-view-pictures-slider__control-outter prev">
        <PrevButton
          onClick={e => {
            e.stopPropagation();
            handlePrev();
          }}
          className={`artwork-view-pictures-slider__control ${
            safeIndex === 0 ? 'disabled' : ''
          }`}
        />
      </div>
      <div className="artwork-view-pictures-section__slide-indicators">
        {images.map((img, idx) => (
          <div
            key={img.uploadId || img.image_id || img.pictureId}
            className={`artwork-view-pictures-section__slide-indicator${
              safeIndex === idx ? ' active' : ''
            }`}
          />
        ))}
      </div>
      <div className="artwork-view-pictures-slider__control-outter next">
        <NextButton
          onClick={e => {
            e.stopPropagation();
            handleNext();
          }}
          className={`artwork-view-pictures-slider__control ${
            safeIndex === images.length - 1 ? 'disabled' : ''
          }`}
        />
      </div>
    </div>
  );

  const updateCaption = (groupId, pictureId, newCaption) => {
    onCaptionChange?.(groupId, pictureId, newCaption);
  };

  const removeSlide = (pictureId) => {
    onRemoveImage(groupId, pictureId);
  };

  return (
    <div className="inline-image-carousel post-embedded-block">
      <div className="inline-image-carousel__image-container">
        <InlinePostImage
          groupId={groupId}
          key={currentImage.pictureId}
          image={currentImage}
          onRemove={() => removeSlide(currentImage.pictureId)}
          onCaptionChange={updateCaption}
          previewMode={previewMode}
          isEmbeddedBlock={false}
          size={1200}
        />
      </div>
      {/* bottom-of-carousel copy – one per carousel, never re-mounted */}
      {images.length > 1 && (
        <div className="inline-image-carousel__controls">
          {sliderContainer}
        </div>
      )}
    </div>
  );
}
